import { useQuery } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { recipesCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { recipesApi } from '../api/recipes'
import { RecipeCatalog } from '../components/recipes/RecipeCatalog'
import { RecipeInstallCard } from '../components/recipes/RecipeInstallCard'
import { RecipeSetupCard } from '../components/recipes/RecipeSetupCard'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useMeQuery } from '../hooks/queries'

/**
 * The workspace's recipes: what is set up (with its setup open under
 * `?install=`) and what can be (`?setup=<recipe>[&source=]`). `?agent=`
 * carries an agent created from a setup back into it.
 */
export function RecipesPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>()
  const { data: me } = useMeQuery()
  const wsName =
    me?.workspaces.find((w) => w.workspace_id === workspaceId)?.name ??
    recipesCopy.workspaceFallback
  const [params, setParams] = useSearchParams()
  const recipesQuery = useQuery({
    queryKey: ['recipes', workspaceId],
    queryFn: () => recipesApi.list(workspaceId),
    enabled: !!workspaceId,
  })
  const installsQuery = useQuery({
    queryKey: ['recipe-installs', workspaceId],
    queryFn: () => recipesApi.installs(workspaceId),
    enabled: !!workspaceId,
  })
  const recipes = recipesQuery.data ?? []
  const installs = installsQuery.data ?? []
  const installsSkeleton = useSkeletonDelay(installsQuery.isPending)
  const recipesSkeleton = useSkeletonDelay(recipesQuery.isPending)

  const open = (next: Record<string, string>) =>
    setParams(new URLSearchParams(next), { replace: true })
  const close = () => open({})
  const setup = params.get('setup')
  const editing = params.get('install')
  const setupRecipe = recipes.find((r) => r.id === setup)

  const retry = (refetch: () => Promise<unknown>) => (
    <Button variant="outline" size="sm" onClick={() => void refetch()}>
      {recipesCopy.retry}
    </Button>
  )

  return (
    <WsPageShell
      crumbs={[
        { label: wsName.toLowerCase(), to: ROUTES.home },
        { label: recipesCopy.crumb },
      ]}
      title={recipesCopy.title}
    >
      <Stack gap="space-24">
        <Stack as="section" gap="space-12" aria-busy={installsQuery.isPending}>
          <SectionHeader rule trailingSpacing="compact">
            {recipesCopy.sections.installed}
          </SectionHeader>
          {installsQuery.isPending ? (
            installsSkeleton && <Skeleton lines={3} />
          ) : installsQuery.isError ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline">
                {recipesCopy.installsLoadFailed}
                {retry(installsQuery.refetch)}
              </Inline>
            </Alert>
          ) : installs.length === 0 ? (
            <EmptyState
              variant="inline"
              description={recipesCopy.installed.empty}
            />
          ) : (
            installs.map((i) =>
              editing === i.id ? (
                <RecipeSetupCard
                  key={i.id}
                  workspaceId={workspaceId}
                  install={i}
                  recipe={recipes.find((r) => r.id === i.recipe_id)}
                  onClose={close}
                />
              ) : (
                <RecipeInstallCard
                  key={i.id}
                  workspaceId={workspaceId}
                  install={i}
                  onEdit={() => open({ install: i.id })}
                />
              ),
            )
          )}
        </Stack>

        <Stack as="section" gap="space-12" aria-busy={recipesQuery.isPending}>
          <SectionHeader rule trailingSpacing="compact">
            {recipesCopy.sections.available}
          </SectionHeader>
          {recipesQuery.isPending ? (
            recipesSkeleton && <Skeleton lines={3} />
          ) : recipesQuery.isError ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline">
                {recipesCopy.loadFailed}
                {retry(recipesQuery.refetch)}
              </Inline>
            </Alert>
          ) : setupRecipe ? (
            <RecipeSetupCard
              key={setupRecipe.id}
              workspaceId={workspaceId}
              recipe={setupRecipe}
              initialSourceId={params.get('source') ?? undefined}
              onClose={close}
            />
          ) : (
            <RecipeCatalog
              recipes={recipes}
              onSetUp={(id) => open({ setup: id })}
            />
          )}
        </Stack>
      </Stack>
    </WsPageShell>
  )
}
